import { useMemo } from 'react'
import { useGetLuckyDrawPool } from '../../api/lucky-draw'
import { buildCards, liveCards, undrawnCards } from './draw'

// Stable identity — a fresh [] each render would rebuild the wall's timers.
const NO_ITEMS = []

// A refetch mid-draw hands the wall new arrays and restarts the animation. Refetched explicitly
// on an award instead. Exported because the draw query is frozen for the same reason.
export const NO_BACKGROUND_REFETCH = { refetchOnWindowFocus: false, refetchOnReconnect: false }

/** Each company's remaining prize allowance, keyed for the wall. */
const toHeadroom = (eligible = []) =>
  new Map(eligible.map((entry) => [entry.companyKey, entry.remainingWins]))

/**
 * One prize's wall: the cards, who may still win, and which tickets are spent. `slots` is the
 * prize's batch size.
 *
 * Derived from the query rather than mirrored into state: the refetch after an award is the one
 * writer, and a prize change is simply a new key.
 */
export const useWall = (id, prizeId, slots) => {
  const {
    data: pool,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetLuckyDrawPool(id, prizeId, { ...NO_BACKGROUND_REFETCH, enabled: Boolean(prizeId) })

  const cards = useMemo(() => buildCards(pool?.entries ?? NO_ITEMS), [pool?.entries])
  const headroom = useMemo(() => toHeadroom(pool?.eligible), [pool?.eligible])
  const wonTickets = pool?.wonTickets ?? NO_ITEMS
  const won = useMemo(() => new Set(wonTickets.map((entry) => entry.ticket)), [wonTickets])

  // What the flashes travel. Every undrawn card, so a capped company's tickets still light up.
  const undrawn = useMemo(() => undrawnCards(cards, won), [cards, won])
  // What the landing is confined to. Here, not in the wall, so START is gated on the same list: a
  // company under its cap with every ticket drawn still looks eligible, and would light a dead wall.
  const live = useMemo(() => liveCards(cards, headroom, won), [cards, headroom, won])

  // Cards on the wall and units left, but nothing winnable. cards.length matters: a group whose
  // companies hold none of its ticket types is misconfigured, not exhausted.
  const isExhausted = Boolean(pool) && !isFetching && slots >= 1 && cards.length > 0 && live.length === 0

  return {
    cards,
    headroom,
    won,
    wonTickets,
    undrawn,
    live,
    isExhausted,
    cap: pool?.cap,
    // The pool these cards came from, sent back with each press.
    poolVersion: pool?.poolVersion,
    isLoading,
    isFetching,
    error,
    refetch,
  }
}
